import * as impl from './impl.ts';
import { authenticateSecret, Authenticator } from './core.ts';
import { randomId } from '../common/ids.ts';
import { sign, unsign } from '../common/jwt.ts';
import type { Authentication, AuthenticationRequest, AuthenticationResponse } from './types.ts';
import { exceptionInfo, exceptionMessage } from '../common/types.ts';
import { getLogger } from '../logger.ts';

const log = getLogger('gateway.auth.basic');
async function authenticate({authentication}: AuthenticationRequest,
                            secret: string,
                            ttl: number,
                            secretVerifier?: (username?: string, password?: string) => Promise<boolean>,
                            usernameResolver?: (login?: string) => Promise<string> | string): Promise<AuthenticationResponse> {
    switch (authentication?.method) {
        case 'secret': {
            const login = authentication.login;
            const resolvedUsername = usernameResolver ? await usernameResolver(login) : login;
            if (secretVerifier) {
                if (!(await secretVerifier(resolvedUsername, authentication.secret))) {
                    throw exceptionInfo("Invalid login/secret", {type: 'failure', message: 'Invalid login/secret'});
                }
            }
            const response = await authenticateSecret(authentication, resolvedUsername);
            const accessToken = sign({
                user: response.user,
                exp: Math.floor((Date.now() + ttl) / 1000 )
            }, secret);
            return {...response, accessToken}

        }
        case 'access-token': {
            try {
                const token = (authentication as Required<Pick<Authentication, 'token'>>).token;
                const user = (unsign(token, secret)).user as string | undefined;
                // also consider now/ttl
                return {type: 'success', login: user, user, accessToken: token};
            } catch (e) {
                throw exceptionInfo(`Invalid or expired token:${exceptionMessage(e)}`, {type: 'failure', message: 'Invalid or expired token: ' + exceptionMessage(e)}, e);
            }
        }
        default: {
            const msg = `Unknown authentication method '${authentication?.method}'`;
            log.debug(msg);
            throw exceptionInfo(msg, {type: 'failure', message: msg});
        }
    }
}

class BasicAuthenticator implements impl.AuthImpl {
    readonly #secret: string;
    readonly #ttl: number;
    readonly #secretVerifier?: (user?: string, password?: string) => Promise<boolean>;
    readonly #usernameResolver?: (login?: string) => Promise<string> | string;

    constructor(
        secret: string,
        ttl: number,
        secretVerifier?: (username?: string, password?: string) => Promise<boolean>,
        usernameResolver?: (login?: string) => Promise<string> | string) {
        this.#secret = secret;
        this.#ttl = ttl;
        this.#secretVerifier = secretVerifier;
        this.#usernameResolver = usernameResolver;
    }

    auth(authenticationRequest: AuthenticationRequest) {
        return authenticate(authenticationRequest, this.#secret, this.#ttl, this.#secretVerifier, this.#usernameResolver);
    }
}

export interface BasicAuthenticatorConfig extends impl.DefaultAuthenticatorConfig {
    ttl?: number,
    secretVerifier?: (login?: string, secret?: string) => Promise<boolean>,
    usernameResolver?: (login?: string) => Promise<string> | string
}

export function authenticator(config: BasicAuthenticatorConfig): Authenticator {
    const ttl = 1000 * (config.ttl ?? 60000);
    const secretVerifier = config.secretVerifier;
    const usernameResolver = config.usernameResolver;
    if (log.enabledFor('debug')) {
        log.debug(`creating basic authenticator: ttl: ${ttl}, secret verifier: ${secretVerifier !== undefined}, username resolver: ${usernameResolver !== undefined}`);
    }

    return impl.authenticator(config, new BasicAuthenticator(randomId(), ttl, secretVerifier, usernameResolver));
}
